import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { TrendingUp, RefreshCw } from "lucide-react";

/**
 * Live FX rates admin card — shows the current in-memory rates,
 * timestamp of the last successful fetch, and a manual "Refresh now"
 * button that calls POST /api/admin/fx-rates/refresh.
 *
 * Backend fetches from open.er-api.com daily (see fx_service.py).
 */
const FxRatesCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/fx-rates");
      setData(r.data);
    } catch (err) {
      console.debug("[fx-rates] load skipped:", err?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/admin/fx-rates/refresh");
      setMsg({
        ok: !!r.data.ok,
        text: r.data.ok
          ? `Rates refreshed from open.er-api.com`
          : `Refresh failed: ${r.data.error || "unknown error"} — using last cached rates`,
      });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Refresh failed" });
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div data-testid="admin-fx-card" className="bg-white rounded-2xl soft-shadow p-6">
        <p className="text-sm text-navy-700/50">Loading FX rates…</p>
      </div>
    );
  }

  const rates = data.rates || {};
  const lastAt = data.cached_updated_at;

  return (
    <div data-testid="admin-fx-card" className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-coral-400" />
          <h2 className="font-semibold text-navy-700 text-lg">Live FX rates</h2>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          data-testid="admin-fx-refresh-btn"
          className="inline-flex items-center gap-1.5 border border-navy-700/20 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100 disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Refreshing…" : "Refresh now"}
        </button>
      </div>
      <p className="text-sm text-navy-700/60 -mt-2">
        USD-based conversion rates powering multi-currency Stripe checkout. Auto-refreshes daily from <a href="https://open.er-api.com" target="_blank" rel="noreferrer" className="text-coral-500 underline">open.er-api.com</a>.
      </p>

      {msg && (
        <div
          data-testid="admin-fx-msg"
          className={`text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {Object.entries(rates).map(([code, val]) => (
          <div
            key={code}
            data-testid={`fx-rate-${code}`}
            className="rounded-lg bg-cream-100 px-3 py-2 text-center"
          >
            <div className="text-[10px] uppercase tracking-wider text-navy-700/60 font-semibold">{code}</div>
            <div className="text-navy-700 font-bold text-sm mt-0.5">
              {typeof val === "number" ? (code === "NGN" ? Math.round(val).toLocaleString() : val.toFixed(2)) : "—"}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-navy-700/50">
        {lastAt ? `Last updated ${new Date(lastAt).toLocaleString()}` : "Using in-memory defaults (no cached rates yet)"}
      </p>
    </div>
  );
};

export default FxRatesCard;
